import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { combineSx, createStyleSheet, globalStyles } from "../utils/styles";

import CareAndSupportIcon from "../../assets/category_care_and_support.svg?react";
import ClientProfileIcon from "../../assets/category_client_profile.svg?react";
import GeneralHealthIcon from "../../assets/category_general_health.svg?react";
import HivInformationIcon from "../../assets/category_hiv_information.svg?react";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import LightTooltip from "./LightTooltip";
import MentalHealthAndSafetyIcon from "../../assets/category_mental_health_and_safety.svg?react";
import OtherIcon from "../../assets/flag_unknown.svg?react";
import PartnerInformationIcon from "../../assets/category_partner_information.svg?react";
import {
  PatientDetailData,
  PatientProfileData,
} from "../features/data/patientApiSlice";
import SexualHealthAndBehaviorIcon from "../../assets/category_sexual_health_and_behavior.svg?react";
import TbInformationIcon from "../../assets/category_tb_information.svg?react";
import { computeReadableValue } from "../utils/strings";
import { formatDateTime } from "../utils/dates";
import { useTranslation } from "react-i18next";
import {
  createAggregatedFieldsSet,
  aggregateFieldData,
  type AggregatedItem,
  type ExtractedDataEntry,
} from "../utils/dataAggregation";
import { AggregatedFieldRow } from "./AggregatedFieldRow";
import ViewClientPhoneNumber from "./ViewClientPhoneNumber";

enum CollectedDataCategory {
  ClientProfile = "clientProfile",
  CareAndSupport = "careAndSupport",
  HivInformation = "hivInformation",
  TbInformation = "tbInformation",
  SexualHealthAndBehavior = "sexualHealthAndBehavior",
  MentalHealthAndSafety = "mentalHealthAndSafety",
  GeneralHealth = "generalHealth",
  PartnerInformation = "partnerInformation",
  Other = "other",
  Unused = "unused", // For data that is not currently displayed
}

const categoryUxMap: {
  [key in CollectedDataCategory]: {
    color: string;
    icon: React.FunctionComponent<
      React.SVGProps<SVGSVGElement> & {
        title?: string;
      }
    >;
  };
} = {
  [CollectedDataCategory.ClientProfile]: {
    color: "#3cad3c",
    icon: ClientProfileIcon,
  },
  [CollectedDataCategory.CareAndSupport]: {
    color: "#076ba8",
    icon: CareAndSupportIcon,
  },
  [CollectedDataCategory.HivInformation]: {
    color: "#ea2726",
    icon: HivInformationIcon,
  },
  [CollectedDataCategory.TbInformation]: {
    color: "#dd9232",
    icon: TbInformationIcon,
  },
  [CollectedDataCategory.SexualHealthAndBehavior]: {
    color: "#5e597e",
    icon: SexualHealthAndBehaviorIcon,
  },
  [CollectedDataCategory.MentalHealthAndSafety]: {
    color: "#c37eb3",
    icon: MentalHealthAndSafetyIcon,
  },
  [CollectedDataCategory.GeneralHealth]: {
    color: "#1876d2",
    icon: GeneralHealthIcon,
  },
  [CollectedDataCategory.PartnerInformation]: {
    color: "#3daf94",
    icon: PartnerInformationIcon,
  },
  [CollectedDataCategory.Other]: {
    color: "#795548",
    icon: OtherIcon,
  },
  [CollectedDataCategory.Unused]: {
    color: "#000000",
    icon: OtherIcon, // Using OtherIcon for unused data
  },
};

const dataToCategoryMap: {
  [key: string]: CollectedDataCategory;
} = {
  full_name: CollectedDataCategory.ClientProfile,
  age: CollectedDataCategory.ClientProfile,
  biological_sex: CollectedDataCategory.ClientProfile,
  occupation: CollectedDataCategory.ClientProfile,
  date_of_birth: CollectedDataCategory.ClientProfile,
  clinic_folder_number: CollectedDataCategory.ClientProfile,
  partner_vmmc_status: CollectedDataCategory.ClientProfile,
  education: CollectedDataCategory.ClientProfile,
  referral_word_present: CollectedDataCategory.ClientProfile,
  peer_referral_code: CollectedDataCategory.ClientProfile,
  attribution_source: CollectedDataCategory.ClientProfile,

  connected_to_any_care: CollectedDataCategory.CareAndSupport,
  facility_type: CollectedDataCategory.CareAndSupport,
  facility_visited: CollectedDataCategory.CareAndSupport,
  date_facility_visited: CollectedDataCategory.CareAndSupport,
  support_system: CollectedDataCategory.CareAndSupport,
  independent_income: CollectedDataCategory.CareAndSupport,
  food_insecurity: CollectedDataCategory.CareAndSupport,
  housing_instability: CollectedDataCategory.CareAndSupport,
  barriers_to_care: CollectedDataCategory.CareAndSupport,
  care_linkage_clinic_visit: CollectedDataCategory.CareAndSupport,
  care_linkage_health_appointment: CollectedDataCategory.CareAndSupport,
  care_linkage_hiv_testing: CollectedDataCategory.CareAndSupport,
  care_linkage_sti_testing: CollectedDataCategory.CareAndSupport,
  care_linkage_pregnancy: CollectedDataCategory.CareAndSupport,
  care_linkage_hiv_treatment: CollectedDataCategory.CareAndSupport,
  care_linkage_sti_treatment: CollectedDataCategory.CareAndSupport,
  care_linkage_prep_refill: CollectedDataCategory.CareAndSupport,
  care_linkage_art_refill: CollectedDataCategory.CareAndSupport,
  care_linkage_contraception: CollectedDataCategory.CareAndSupport,
  care_linkage_pep: CollectedDataCategory.CareAndSupport,
  care_linkage_starting_prep: CollectedDataCategory.CareAndSupport,
  care_linkage_starting_pep: CollectedDataCategory.CareAndSupport,
  care_linkage_crisis_pregnancy_abortion: CollectedDataCategory.CareAndSupport,
  care_linkage_vaccine_injection: CollectedDataCategory.CareAndSupport,
  care_linkage_social_services: CollectedDataCategory.CareAndSupport,
  care_linkage_gynecology: CollectedDataCategory.CareAndSupport,
  care_linkage_tb: CollectedDataCategory.CareAndSupport,
  main_clinic: CollectedDataCategory.CareAndSupport,

  hiv_status: CollectedDataCategory.HivInformation,
  last_hiv_test_absolute: CollectedDataCategory.HivInformation,
  last_hiv_test_result: CollectedDataCategory.HivInformation,
  last_hiv_test: CollectedDataCategory.HivInformation,
  recent_hiv_exposure: CollectedDataCategory.HivInformation,
  hiv_under_treatment: CollectedDataCategory.HivInformation,
  hiv_treatment_side_effects: CollectedDataCategory.HivInformation,
  takes_pep: CollectedDataCategory.HivInformation,
  takes_prep: CollectedDataCategory.HivInformation,
  requests_prep_or_pep: CollectedDataCategory.HivInformation,
  viral_load_test_result: CollectedDataCategory.HivInformation,
  viral_load_test_result_absolute: CollectedDataCategory.HivInformation,
  viral_load_test_last: CollectedDataCategory.HivInformation,
  prescribed_arvs: CollectedDataCategory.HivInformation,
  art_initiation_date: CollectedDataCategory.HivInformation,
  art_interruption: CollectedDataCategory.HivInformation,
  prep_initiation_date: CollectedDataCategory.HivInformation,
  hiv_diagnosis_date: CollectedDataCategory.HivInformation,
  prep_type: CollectedDataCategory.HivInformation,
  oral_prep_7day_adherence: CollectedDataCategory.HivInformation,
  interest_injectable_prep: CollectedDataCategory.HivInformation,
  prior_prep_use: CollectedDataCategory.HivInformation,
  prior_art_use: CollectedDataCategory.HivInformation,

  interest_concern_tb: CollectedDataCategory.TbInformation,
  last_tb_result: CollectedDataCategory.TbInformation,
  last_tb_test: CollectedDataCategory.TbInformation,
  tb_treatment_prescribed: CollectedDataCategory.TbInformation,
  tb_treatment_current: CollectedDataCategory.TbInformation,
  tb_treatment_side_effects: CollectedDataCategory.TbInformation,
  tb_treatment_questions: CollectedDataCategory.TbInformation,
  tb_treatment_ever_completed: CollectedDataCategory.TbInformation,
  tpt_current: CollectedDataCategory.TbInformation,
  tpt_ever_completed: CollectedDataCategory.TbInformation,
  prescribed_tpt: CollectedDataCategory.TbInformation,

  condom_usage_frequency: CollectedDataCategory.SexualHealthAndBehavior,
  condom_usage_frequency_paying: CollectedDataCategory.SexualHealthAndBehavior,
  condom_usage_frequency_permanent:
    CollectedDataCategory.SexualHealthAndBehavior,
  num_sexual_partners: CollectedDataCategory.SexualHealthAndBehavior,
  num_sexual_partners_month: CollectedDataCategory.SexualHealthAndBehavior,
  num_sexual_partners_paying: CollectedDataCategory.SexualHealthAndBehavior,
  num_sexual_partners_permanent: CollectedDataCategory.SexualHealthAndBehavior,
  num_female_partners: CollectedDataCategory.SexualHealthAndBehavior,
  num_male_partners: CollectedDataCategory.SexualHealthAndBehavior,
  sexual_concurrency: CollectedDataCategory.SexualHealthAndBehavior,
  sex_with_hiv_positive: CollectedDataCategory.SexualHealthAndBehavior,
  sex_under_influence: CollectedDataCategory.SexualHealthAndBehavior,
  under_influence_condom_use: CollectedDataCategory.SexualHealthAndBehavior,
  peer_influence_sex: CollectedDataCategory.SexualHealthAndBehavior,
  sex_trade_worker: CollectedDataCategory.SexualHealthAndBehavior,
  new_to_sex_work: CollectedDataCategory.SexualHealthAndBehavior,
  potential_sti: CollectedDataCategory.SexualHealthAndBehavior,
  diagnosed_sti: CollectedDataCategory.SexualHealthAndBehavior,
  sti: CollectedDataCategory.SexualHealthAndBehavior,
  sti_treatment: CollectedDataCategory.SexualHealthAndBehavior,
  interest_in_vmmc: CollectedDataCategory.SexualHealthAndBehavior,
  vmmc_completion: CollectedDataCategory.SexualHealthAndBehavior,
  barriers_to_condoms: CollectedDataCategory.SexualHealthAndBehavior,
  sexual_activities: CollectedDataCategory.SexualHealthAndBehavior,
  pregnancy_status: CollectedDataCategory.SexualHealthAndBehavior,

  physical_assault: CollectedDataCategory.MentalHealthAndSafety,
  sexually_abused: CollectedDataCategory.MentalHealthAndSafety,
  feel_depressed_or_hopeless: CollectedDataCategory.MentalHealthAndSafety,
  low_interest_in_doing_anything: CollectedDataCategory.MentalHealthAndSafety,
  drinking_or_drug_problem: CollectedDataCategory.MentalHealthAndSafety,
  uses_drugs: CollectedDataCategory.MentalHealthAndSafety,
  uses_needles: CollectedDataCategory.MentalHealthAndSafety,
  shared_needles: CollectedDataCategory.MentalHealthAndSafety,
  shared_needles_with_hiv_positive: CollectedDataCategory.MentalHealthAndSafety,
  future_goals: CollectedDataCategory.MentalHealthAndSafety,
  childhood_sexual_abuse: CollectedDataCategory.MentalHealthAndSafety,
  care_linkage_mental_health: CollectedDataCategory.MentalHealthAndSafety,
  violence: CollectedDataCategory.MentalHealthAndSafety,

  symptoms: CollectedDataCategory.GeneralHealth,
  conditions: CollectedDataCategory.GeneralHealth,
  medications: CollectedDataCategory.GeneralHealth,
  uti: CollectedDataCategory.GeneralHealth,
  travel_frequency_all: CollectedDataCategory.GeneralHealth,
  work_travel_frequency: CollectedDataCategory.GeneralHealth,

  partner_age: CollectedDataCategory.PartnerInformation,
  partner_emotional_control: CollectedDataCategory.PartnerInformation,
  partner_sexual_concurrency: CollectedDataCategory.PartnerInformation,
  partner_hiv_status: CollectedDataCategory.PartnerInformation,
  partner_hiv_treatment: CollectedDataCategory.PartnerInformation,
  partner_on_art: CollectedDataCategory.PartnerInformation,

  // Unused data
  language: CollectedDataCategory.Unused,
  ceshhar_id: CollectedDataCategory.Unused,
  referral_word: CollectedDataCategory.Unused,
  referral_code: CollectedDataCategory.Unused,
  attribution_source_matched: CollectedDataCategory.Unused,
  topic_progression: CollectedDataCategory.Unused,
};

const categoryOrder: CollectedDataCategory[][] = [
  [CollectedDataCategory.ClientProfile, CollectedDataCategory.CareAndSupport],
  [CollectedDataCategory.HivInformation, CollectedDataCategory.TbInformation],
  [
    CollectedDataCategory.SexualHealthAndBehavior,
    CollectedDataCategory.MentalHealthAndSafety,
  ],
  [
    CollectedDataCategory.GeneralHealth,
    CollectedDataCategory.PartnerInformation,
  ],
  [CollectedDataCategory.Other], // This will catch any other data that doesn't fit into the above categories
];

const clientPhoneCategory = {
  name: "client_phone_number",
  value: null,
  description: "Client Phone Number",
  last_updated: "",
  isAggregated: false,
};

interface CategoryDataItem {
  name: string;
  value: string | null;
  description: string;
  last_updated: string;
  isAggregated?: boolean;
  aggregatedItems?: AggregatedItem[];
}

const PatientCollectedData = (props: {
  patientProfileData: PatientProfileData | null;
  patient: PatientDetailData;
}) => {
  const { patientProfileData, patient } = props;
  const { t } = useTranslation("Patient");

  const aggregatedFields = useMemo(
    () =>
      createAggregatedFieldsSet(
        dataToCategoryMap,
        patientProfileData?.extracted_data
          ?.filter((entry) => (entry.history?.length ?? 0) > 0)
          .map((entry) => entry.name)
      ),
    [patientProfileData]
  );

  const aggregatedFieldsMap = useMemo(() => {
    const map = new Map<string, AggregatedItem[]>();
    if (!patientProfileData?.extracted_data) return map;

    const fieldEntries = new Map<string, ExtractedDataEntry[]>();
    patientProfileData.extracted_data
      .filter((entry) => aggregatedFields.has(entry.name))
      .forEach((entry) => {
        const entries = fieldEntries.get(entry.name) ?? [];
        const historyEntries =
          entry.history && entry.history.length > 0
            ? entry.history.map((historyItem) => ({
              value: historyItem.value,
              description: entry.description,
              last_updated: historyItem.last_updated,
            }))
            : [
              {
                value: entry.value,
                description: entry.description,
                last_updated: entry.last_updated,
              },
            ];
        entries.push(...historyEntries);
        fieldEntries.set(entry.name, entries);
      });

    fieldEntries.forEach((entries, fieldName) => {
      const aggregated = aggregateFieldData(entries);
      if (aggregated.length > 0) {
        map.set(fieldName, aggregated);
      }
    });

    return map;
  }, [patientProfileData, aggregatedFields]);

  const categoryMap: Map<CollectedDataCategory, CategoryDataItem[]> =
    useMemo(() => {
      const map = Object.keys(dataToCategoryMap).reduce((acc, name) => {
        const category = dataToCategoryMap[name] || CollectedDataCategory.Other;
        if (category !== CollectedDataCategory.Unused) {
          if (aggregatedFields.has(name) && aggregatedFieldsMap.has(name)) {
            if (!acc.has(category)) {
              acc.set(category, []);
            }
            const aggregatedItems = aggregatedFieldsMap.get(name) ?? [];
            const mostRecentDate = aggregatedItems[0]?.date;
            const mostRecentEntry = patientProfileData?.extracted_data
              ?.filter((e) => e.name === name)
              .sort(
                (a, b) =>
                  new Date(b.last_updated).getTime() -
                  new Date(a.last_updated).getTime()
              )[0];
            acc.get(category)?.push({
              name,
              value: null,
              description: mostRecentEntry?.description || name,
              last_updated: mostRecentDate
                ? mostRecentDate.toISOString()
                : mostRecentEntry?.last_updated || "",
              isAggregated: true,
              aggregatedItems,
            });
          } else {
            const item = patientProfileData?.extracted_data?.find(
              (item) => item.name === name
            );
            if (item) {
              if (!acc.has(category)) {
                acc.set(category, []);
              }
              const { value, description, last_updated } = item;
              acc.get(category)?.push({ name, value, description, last_updated, isAggregated: false });
            }
          }
        }
        return acc;
      }, new Map<CollectedDataCategory, CategoryDataItem[]>());

      const clientProfileCategory = CollectedDataCategory.ClientProfile;
      if (!map.has(clientProfileCategory)) {
        map.set(clientProfileCategory, []);
      }
      map.get(clientProfileCategory)?.push(clientPhoneCategory);
      return map;
    }, [patientProfileData, aggregatedFieldsMap]);

  return (
    <Stack direction="column" sx={styles.container} spacing={2}>
      {categoryMap.size > 0 ? (
        <>
          <Stack
            direction="row"
            sx={combineSx(globalStyles.gapTop, styles.centerAlignedRow)}
          >
            <InfoIcon color="primary" fontSize="small" />
            <Typography variant="detailCellTitle" sx={styles.aboutText}>
              {t("collectedDataInfo")}
            </Typography>
          </Stack>
          {categoryOrder.map(
            (categoryRow) =>
              categoryRow.some((category) => categoryMap.has(category)) && (
                <Box sx={styles.rowContainer} key={categoryRow[0] + "-row"}>
                  {categoryRow.map((category) =>
                    categoryMap.has(category) ? (
                      <Box
                        key={category}
                        sx={{
                          ...styles.categoryBox,
                          flex: 1,
                        }}
                      >
                        <Stack direction={"row"} sx={styles.centerAlignedRow}>
                          {React.createElement(categoryUxMap[category].icon, {
                            style: {
                              fill: categoryUxMap[category].color,
                            },
                          })}
                          <Typography
                            variant="h6"
                            sx={{
                              ...styles.categoryTitleText,
                              color: categoryUxMap[category].color,
                            }}
                          >
                            {t(`collectedDataCategories:${category}`)}
                          </Typography>
                        </Stack>
                        <TableContainer sx={{ width: "fit-content" }}>
                          <Table>
                            <TableBody>
                              {categoryMap.get(category)?.map((data) => {
                                if (data.isAggregated && data.aggregatedItems) {
                                  return (
                                    <AggregatedFieldRow
                                      key={data.name}
                                      fieldName={data.name}
                                      description={data.description}
                                      aggregatedItems={data.aggregatedItems}
                                    />
                                  );
                                }

                                return (
                                  <LightTooltip
                                    sx={
                                      data.name === clientPhoneCategory.name
                                        ? { display: "none" }
                                        : undefined
                                    }
                                    key={data.name}
                                    title={
                                      <Stack direction="column">
                                        {data.description &&
                                        data.description !== data.name ? (
                                          <Typography
                                            variant="tooltip"
                                            sx={styles.tooltipDescription}
                                          >
                                            {data.description}
                                          </Typography>
                                        ) : null}
                                        <Typography
                                          variant="tooltip"
                                          sx={styles.tooltipLastUpdated}
                                        >
                                          {t("collectedDataLastUpdated", {
                                            date: formatDateTime(
                                              new Date(data.last_updated)
                                            ),
                                          })}
                                        </Typography>
                                      </Stack>
                                    }
                                    disableInteractive
                                    placement="bottom-start"
                                    slotProps={{
                                      popper: {
                                        modifiers: [
                                          {
                                            name: "offset",
                                            options: {
                                              offset: [0, -12],
                                            },
                                          },
                                        ],
                                      },
                                    }}
                                  >
                                    <TableRow sx={{ width: "100%" }}>
                                      <TableCell sx={styles.plainCell}>
                                        <Typography
                                          component="ul"
                                          sx={styles.descriptionTextContainer}
                                        >
                                          <Typography
                                            variant="body2"
                                            component="li"
                                            sx={{ listStyleType: "disc" }}
                                          >
                                            {computeReadableValue(data.name)}:
                                          </Typography>
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={styles.plainCell}>
                                        {data.name === clientPhoneCategory.name ? (
                                          patient.has_phone_number ? (
                                            <ViewClientPhoneNumber
                                              patient={patient}
                                            />
                                          ) : (
                                            <LightTooltip
                                              title={
                                                <Typography
                                                  variant="tooltip"
                                                  sx={styles.tooltipDescription}
                                                >
                                                  {t("optedOutDescription")}
                                                </Typography>
                                              }
                                            >
                                              <Typography
                                                variant="body2bold"
                                                sx={{ marginLeft: 1 }}
                                              >
                                                {t("optedOutTitle")}
                                              </Typography>
                                            </LightTooltip>
                                          )
                                        ) : (
                                          <Typography
                                            variant="body2bold"
                                            sx={{ marginLeft: 1 }}
                                          >
                                            {computeReadableValue(
                                              data.value || t("common:unknown")
                                            )}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  </LightTooltip>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : null
                  )}
                </Box>
              )
          )}
        </>
      ) : (
        <Typography variant="body2">{t("noCollectedData")}</Typography>
      )}
    </Stack>
  );
};

const styles = createStyleSheet({
  container: {
    ...globalStyles.gapTop,
  },
  centerAlignedRow: {
    alignItems: "center",
  },
  aboutText: {
    marginLeft: 1,
    fontStyle: "italic",
    color: "primary.main",
  },
  rowContainer: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: 2,
  },
  categoryBox: {
    border: (theme) => `1px solid ${theme.palette.divider}`,
    padding: 2,
    paddingRight: 3,
  },
  categoryTitleText: {
    marginLeft: 1,
  },
  descriptionTextContainer: {
    paddingLeft: 2,
    color: "GrayText",
  },
  plainCell: {
    padding: 0,
    borderBottom: "none",
    verticalAlign: "top",
  },
  tooltipDescription: {
    marginBottom: 1,
  },
  tooltipLastUpdated: {
    fontStyle: "italic",
    color: (theme) => theme.palette.text.secondary,
    fontWeight: (theme) => theme.typography.fontWeightRegular,
  },
});

export default PatientCollectedData;
